<script>
import RawData from "@/components/widgets/raw-data.vue";
import RemarksModal from "@/components/modals/remarks-modal.vue";
import ProposalItemExpenditure from "@/components/widgets/proposal-item-expenditure.vue";

export default {
    name: "ProposalByOpcr",
    components: {
        RawData,
        RemarksModal,
        ProposalItemExpenditure,
    },
    props: {
        opcrs: {
            type: Array,
            default: () => [],
        },
        loading: {
            type: Boolean,
            default: false,
        },
        du: {
            type: Object,
            required: true,
        },
        campus: {
            type: Object,
            required: true,
        },
        type: {
            type: String,
            required: true,
        },
        viewFrom: {
            type: String,
            default: "planning",
        },
        filter: {
            type: String,
            default: "",
        },
        campIndex: {
            type: Number,
            default: 0,
        },
        duIndex: {
            type: Number,
            default: 0,
        },
        totalCampuses: {
            type: Number,
            default: 1,
        },
        department: {
            type: [Object, String, Boolean],
            default: null,
        },
        selectedTemplate: {
            type: Object,
            default: () => ({}),
        },
        budgets: {
            type: Array,
            default: () => [],
        },
    },
    data() {
        return {
            opcrList: [],
        };
    },
    computed: {
        isActive() {
            return this.type === "active";
        },
        isShortListed() {
            return this.type === "shortlisted";
        },
        isParked() {
            return this.type === "parked";
        },
        isApproved() {
            return this.type === "approved";
        },
        remarksModalId() {
            return `remarks-modal-opcr-${this.campus?.id || this.campIndex}-${this.du?.id || this.duIndex}`;
        },
    },
    watch: {
        opcrs: {
            immediate: true,
            handler(val) {
                this.syncOpcrs(val || []);
            },
        },
    },
    methods: {
        syncOpcrs(list) {
            if (!list || !list.length) {
                this.opcrList = [];
                return;
            }

            this.opcrList = list.map((item, index) => {
                const existing = this.opcrList.find((x) => x.id === item.id);
                const isExpanded = existing ? existing.isExpanded : (index === 0);
                return {
                    ...item,
                    isExpanded: isExpanded,
                    targets: existing ? existing.targets : [],
                    targetsLoaded: existing ? existing.targetsLoaded : false,
                    loadingTargets: existing ? existing.loadingTargets : false,
                    tabIndex: existing ? existing.tabIndex : 0,
                    tabSelect: existing ? existing.tabSelect : "Strategic Priority",
                    searchQuery: existing ? existing.searchQuery : "",
                };
            });

            // Default expand the first row of data of the endpoint and fetch GetAllProposalApis
            if (this.opcrList.length > 0 && this.opcrList[0].isExpanded && !this.opcrList[0].targetsLoaded) {
                this.getOpcrTargets(this.opcrList[0]);
            }
        },
        toggleOpcr(opcr) {
            opcr.isExpanded = !opcr.isExpanded;
            if (opcr.isExpanded && !opcr.targetsLoaded) {
                this.getOpcrTargets(opcr);
            }
        },
        async getOpcrTargets(opcr) {
            if (!opcr || opcr.loadingTargets || opcr.targetsLoaded) return;
            this.$set(opcr, "loadingTargets", true);
            try {
                const cycle = this.selectedTemplate.lockToCycle ? this.selectedTemplate.cycle : this.$store.state.data.cycle;
                const { data: response } = await this.$store.dispatch("planningapi/GetAllProposalApis", {
                    opcrId: opcr.id,
                    cycle: cycle,
                });
                if (response && response.error) {
                    if (this.showToast) {
                        this.showToast(response.errorMessage || response.message, "error");
                    }
                    this.$set(opcr, "targets", []);
                } else {
                    const rawList = Array.isArray(response) ? response : (Array.isArray(response?.data) ? response.data : []);
                    const processed = this.processTargets(rawList);
                    this.$set(opcr, "targets", processed);
                    this.$set(opcr, "targetsLoaded", true);

                    const tabs = this.getTabOptions(opcr);
                    for (const tab of tabs) {
                        if (this.checkTab(opcr, tab)) {
                            this.$set(opcr, "tabSelect", tab);
                            break;
                        }
                    }
                }
            } catch (err) {
                if (this.showToast) {
                    this.showToast("Failed to fetch OPCR targets.", "error");
                }
                this.$set(opcr, "targets", []);
            } finally {
                this.$set(opcr, "loadingTargets", false);
            }
        },
        processTargets(rawTargets) {
            return (rawTargets || []).map((t) => {
                const apis = (t.planningApis || t.apis || []).map((api) => ({
                    ...api,
                    showResources: api.showResources || false,
                    resourceLoading: api.resourceLoading || false,
                    resources: api.resources || [],
                    statusUpdate: api.statusUpdate || "",
                    searchItem: api.searchItem || "",
                }));
                return {
                    ...t,
                    id: t.planningTargetId || t.id,
                    planningTarget: t.planningTarget || t.description,
                    description: t.planningTarget || t.description,
                    showApis: t.showApis || false,
                    planningApis: apis,
                    apis: apis,
                };
            });
        },
        getTabOptions(opcr) {
            const defaults = [
                "Strategic Priority",
                "Core Functions",
                "Mandatory Support Function",
            ];
            const others = [];
            let hasUnclassified = false;
            (opcr.targets || []).forEach((t) => {
                const classification = t.type || t.indicatorItemClassification;
                if (classification) {
                    const isDefault = defaults.some((d) =>
                        classification
                            .toUpperCase()
                            .includes(d.split(" ")[0].toUpperCase())
                    );
                    if (!isDefault && !others.includes(classification)) {
                        others.push(classification);
                    }
                } else {
                    hasUnclassified = true;
                }
            });
            const result = [...defaults, ...others];
            if (hasUnclassified) {
                result.push("Other Targets");
            }
            return result;
        },
        checkTab(opcr, type) {
            if (!opcr.targets || !opcr.targets.length) return false;
            return opcr.targets.some((item) => {
                const classification = item.type || item.indicatorItemClassification;
                if (!classification) {
                    return type === "Other Targets";
                }
                if (type === "Strategic Priority") {
                    return classification.toUpperCase().includes("STRATEGIC");
                } else if (type === "Core Functions") {
                    return classification.toUpperCase().includes("CORE");
                } else if (type === "Mandatory Support Function") {
                    return classification.toUpperCase().includes("MANDATORY");
                }
                return classification.toUpperCase() === type.toUpperCase();
            });
        },
        filteredTargets(opcr, type) {
            let data = (opcr.targets || []).filter((item) => {
                const classification = item.type || item.indicatorItemClassification;
                if (!classification) {
                    return type === "Other Targets";
                }
                if (type === "Strategic Priority") {
                    return classification.toUpperCase().includes("STRATEGIC");
                } else if (type === "Core Functions") {
                    return classification.toUpperCase().includes("CORE");
                } else if (type === "Mandatory Support Function") {
                    return classification.toUpperCase().includes("MANDATORY");
                }
                return classification.toUpperCase() === type.toUpperCase();
            });

            const query = (opcr.searchQuery || "").trim().toLowerCase();
            if (query) {
                data = data.filter((item) => {
                    const targetText = (item.planningTarget || item.description || "").toLowerCase();
                    if (targetText.includes(query)) return true;
                    const apis = item.planningApis || item.apis || [];
                    return apis.some(
                        (a) =>
                            (a.name && a.name.toLowerCase().includes(query)) ||
                            (a.description && a.description.toLowerCase().includes(query))
                    );
                });
            }
            return data;
        },
        toggleApis(target) {
            this.$set(target, "showApis", !target.showApis);
        },
        async toggleResources(api) {
            const nextState = !api.showResources;
            this.$set(api, "showResources", nextState);
            if (nextState && (!api.resources || !api.resources.length)) {
                this.$set(api, "resourceLoading", true);
                try {
                    const res = await this.$store.dispatch(
                        "planningapiresource/GetPlanningApiResourcesByApi",
                        api.id
                    );
                    const response = res && res.data !== undefined ? res.data : res;
                    if (response && response.error) {
                        if (this.showToast) {
                            this.showToast(
                                response.errorMessage || response.message || "Failed to fetch resources.",
                                "error"
                            );
                        }
                        this.$set(api, "resources", []);
                    } else {
                        const list = Array.isArray(response)
                            ? response
                            : Array.isArray(response?.data)
                                ? response.data
                                : [];
                        list.forEach((item) => {
                            let budgetInfo = (this.budgets || []).find((x) => x.id == item.accountGroupId);
                            item.totalQuantity = (item.planningApiResourceDetails || []).reduce((pv, cv) => {
                                return parseFloat(pv) + parseFloat(cv.quantity);
                            }, 0);
                            item.financeTemplateBudgetItem = {
                                fundSource: budgetInfo?.fundSource || "",
                                fundClass: budgetInfo?.fundClassification?.classification || "",
                                fundClassification: budgetInfo?.fundClassification || null,
                                groupName: budgetInfo?.groupName || "",
                                accountGroup: budgetInfo?.groupName || "",
                                accountId: budgetInfo?.id || item.accountGroupId,
                                id: budgetInfo?.id || item.accountGroupId,
                            };
                            item.showRow = true;
                            item.showChild = true;
                            item.filterKey = `${item.itemName} ${item.description}`;
                            item.status = null;
                        });
                        this.$set(api, "resources", list);
                    }
                } catch (err) {
                    if (this.showToast) {
                        this.showToast("Failed to fetch resources.", "error");
                    }
                    this.$set(api, "resources", []);
                } finally {
                    this.$set(api, "resourceLoading", false);
                }
            }
        },
        hasStatus(api, status) {
            if (!api || !api.status) return false;
            return api.status.toLowerCase().includes(status.toLowerCase());
        },
        filteredItems(items, filter) {
            let data = items || [];
            if (filter && filter.trim() !== "") {
                data = data.filter((x) => {
                    const key = x.filterKey || `${x.itemName || ""} ${x.description || ""}`;
                    return key.toUpperCase().includes(filter.toUpperCase());
                });
            }
            return data;
        },
        updateProposalStatus({ proposalId, du, campus, status }) {
            if (status === "approve") {
                this.$emit("approve-proposal", { proposalId, du, campus });
            } else if (status === "return") {
                this.$emit("return-proposal", { proposalId, du, campus });
            } else if (status === "shortlist") {
                this.$emit("shortlist-proposal", { proposalId, du, campus });
            } else if (status === "park") {
                this.$emit("park-proposal", { proposalId, du, campus });
            }
        },
        returnOpcr(opcr, opcrIndex) {
            const opcrTitle = opcr.title || "this OPCR";
            const duName = this.du && this.du.name ? `${this.du.name}'s ` : "";
            this.$refs.remarksModal.open({
                title: `Return OPCR - ${duName}${opcrTitle}`,
                label: "Return Remarks",
                placeholder: "Enter reason for returning OPCR...",
                btnText: "Return",
                btnClass: "btn-warning",
                btnIcon: "bx bx-reply",
                data: { opcr, opcrIndex },
            });
        },
        async submitReturnOpcr(reason, customData) {
            const opcr = customData?.opcr;
            const opcrIndex = customData?.opcrIndex;
            if (!opcr) return;

            this.$set(opcr, "isReturning", true);
            if (this.$refs.remarksModal?.setLoading) {
                this.$refs.remarksModal.setLoading(true);
            }
            if (this.pageLoader) this.pageLoader(true);
            try {
                const targetId = opcr.id || opcr.preOpcrId;
                const res = await this.$store.dispatch(
                    "preopcr/ReturnPreOpcr",
                    {
                        id: targetId,
                        remarks: `${reason}`,
                    }
                );
                const response = res && res.data !== undefined ? res.data : res;
                if (response && response.error) {
                    if (this.showToast) {
                        this.showToast(
                            response.errorMessage || response.message || "Failed to return OPCR.",
                            "error"
                        );
                    }
                } else {
                    if (this.$refs.remarksModal?.hide) {
                        this.$refs.remarksModal.hide();
                    } else {
                        this.$bvModal.hide(this.remarksModalId);
                    }
                    if (this.showToast) {
                        this.showToast(
                            "OPCR successfully returned.",
                            "success"
                        );
                    }
                    const removeIdx =
                        opcrIndex !== undefined && opcrIndex >= 0
                            ? opcrIndex
                            : this.opcrList.findIndex((o) => o.id === opcr.id);
                    if (removeIdx !== -1) {
                        this.opcrList.splice(removeIdx, 1);
                    }
                    if (Array.isArray(this.opcrs)) {
                        const propIdx = this.opcrs.findIndex((o) => o.id === opcr.id);
                        if (propIdx !== -1) {
                            this.opcrs.splice(propIdx, 1);
                        }
                    }
                    this.$emit("return-opcr", opcr);
                }
            } catch (err) {
                if (this.showToast) {
                    this.showToast("Failed to return OPCR.", "error");
                }
            } finally {
                this.$set(opcr, "isReturning", false);
                if (this.$refs.remarksModal?.setLoading) {
                    this.$refs.remarksModal.setLoading(false);
                }
                if (this.pageLoader) this.pageLoader(false);
            }
        },
        viewAttachments(targets) {
            let apis = [];
            for (let target of targets) {
                if (target.planningApis && target.planningApis.length) {
                    target.planningApis.forEach((api) => {
                        apis.push({
                            id: api.id,
                            name: api.name
                        })
                    })
                }
            }
            this.$emit("view-attachments", apis);
        },
    },
};
</script>

<template>
    <div class="proposal-by-opcr">
        <b-card no-body
            :style="{ zIndex: `${totalCampuses - campIndex}${campus.dus ? campus.dus.length - duIndex : 1}` }"
            class="mb-2 shadow-none mt-2">
            <template v-if="loading">
                <div class="d-flex justify-content-center align-items-center py-5">
                    <b-spinner variant="primary" label="Loading OPCR..."></b-spinner>
                    <span class="ms-2">Loading OPCR...</span>
                </div>
            </template>
            <template v-else-if="!opcrList.length">
                <div class="text-center py-4">
                    <p class="text-muted mb-0">No OPCR found for this Delivery Unit.</p>
                </div>
            </template>
            <template v-else>
                <!-- Stack of OPCR Cards -->
                <div role="tablist" v-for="(opcr, opcrIndex) in opcrList" :key="`opcr-${opcr.id}-${opcrIndex}`">
                    <b-card no-body class="mb-2 border shadow-none" :style="{ 'z-index': opcrList.length - opcrIndex }">
                        <b-card-header header-tag="header" role="tab" class="py-2">
                            <div class="d-flex justify-content-between align-items-center cursor-pointer"
                                @click="toggleOpcr(opcr)">
                                <div class="me-3">
                                    <div class="d-flex align-items-center gap-2 flex-wrap">
                                        <b class="text-white font-size-15">
                                            {{ opcr.title }}
                                        </b>
                                        <span v-if="opcr.status" class="badge bg-success font-size-11">
                                            {{ opcr.status }}
                                        </span>
                                        <span v-if="opcr.cycle" class="badge bg-primary font-size-11">
                                            FY {{ opcr.cycle }}
                                        </span>
                                        <span v-if="opcr.isSupplemental" class="badge bg-warning font-size-11">
                                            Supplemental
                                        </span>
                                    </div>
                                    <small class="text-muted" v-if="opcr.targetsLoaded">
                                        Targets Available ({{ (opcr.targets || []).length }})
                                    </small>
                                </div>
                                <div class="d-flex align-items-center">
                                    <b-button variant="warning" size="sm" class="me-2" :disabled="opcr.isReturning"
                                        @click.stop="returnOpcr(opcr, opcrIndex)">
                                        <b-spinner v-if="opcr.isReturning" small class="me-1"></b-spinner>
                                        Return OPCR
                                    </b-button>
                                    <span class="d-flex align-items-center">
                                        <small v-if="!opcr.isExpanded" class="text-muted">
                                            Click here to expand
                                        </small>
                                        <i class="bx bx-chevron-right rotate font-size-18 ms-2"
                                            :class="{ 'rotate-90': opcr.isExpanded }"></i>
                                    </span>
                                </div>
                            </div>
                        </b-card-header>

                        <!-- Expanded OPCR Collapse -->
                        <b-collapse :visible="opcr.isExpanded" role="tabpanel">
                            <b-card-body class="ps-3 pe-2 pt-3 pb-2">
                                <div v-if="opcr.loadingTargets"
                                    class="d-flex justify-content-center align-items-center py-4">
                                    <b-spinner variant="primary" label="Loading OPCR targets..."></b-spinner>
                                    <span class="ms-2">Loading OPCR targets...</span>
                                </div>
                                <div v-else-if="!opcr.targets || !opcr.targets.length" class="text-center py-4">
                                    <p class="text-muted mb-0">No targets found for this OPCR.</p>
                                </div>
                                <div v-else>
                                    <!-- Targets Pills Tabs grouped by classification -->
                                    <div class="position-relative">

                                        <b-tabs content-class="pt-3 pb-3 text-muted" v-model="opcr.tabIndex" pills>
                                            <div v-for="(typeOption, index) in getTabOptions(opcr)" :key="index">
                                                <b-tab v-if="checkTab(opcr, typeOption)"
                                                    @click="opcr.searchQuery = ''; opcr.tabSelect = typeOption;">
                                                    <template v-slot:title>
                                                        <span class="d-inline-block d-sm-none">
                                                            <i class="mdi mdi-clock-time-four-outline"></i>
                                                        </span>
                                                        <span class="d-none d-sm-inline-block">{{ typeOption }}</span>
                                                    </template>
                                                    <div>
                                                        <!-- Filter Search Input -->
                                                        <div
                                                            class="mb-3 d-flex justify-content-between align-items-center">
                                                            <div class="w-300px">
                                                                <b-form-input autocomplete="off"
                                                                    v-model="opcr.searchQuery" type="search"
                                                                    placeholder="Search Target, API..."
                                                                    class="form-control form-control-sm"></b-form-input>
                                                            </div>
                                                        </div>

                                                        <b-card-body class="p-0">
                                                            <div v-if="filteredTargets(opcr, typeOption).length === 0"
                                                                class="text-center py-3">
                                                                <h5 v-if="opcr.searchQuery">Search not found</h5>
                                                                <h5 v-else>No data available</h5>
                                                            </div>

                                                            <!-- Target List inside OPCR -->
                                                            <div class="card mb-2 border shadow-none"
                                                                v-for="(y, yIndex) in filteredTargets(opcr, typeOption)"
                                                                :key="`target-${y.id || y.planningTargetId}-${yIndex}`"
                                                                :style="{ 'z-index': filteredTargets(opcr, typeOption).length + 10 - yIndex }">
                                                                <RawData :data="y"
                                                                    v-if="$store.state.decrypt && $store.state.decrypt.isAdmin()" />
                                                                <b-card-header class="py-2">
                                                                    <div
                                                                        class="d-flex justify-content-between flex-wrap gap-2">
                                                                        <!-- Target Header Left: Number & Description & Actions -->
                                                                        <div class="d-flex align-items-center">
                                                                            <div class="bg-success bg-gradient text-center me-2 py-1 px-2 position-relative rounded"
                                                                                style="min-width: 30px">
                                                                                <h4
                                                                                    class="mb-0 text-white font-weight-bold">
                                                                                    {{ yIndex + 1 }}
                                                                                </h4>
                                                                            </div>
                                                                            <div>
                                                                                <h5
                                                                                    class="card-title font-size-15 mb-1 text-info">
                                                                                    {{ y.planningTarget || y.description
                                                                                        ||
                                                                                        y.planningMainTarget }}
                                                                                </h5>
                                                                                <div
                                                                                    class="d-flex align-items-center gap-2 flex-wrap">
                                                                                    <div>
                                                                                        <small>
                                                                                            <a href="javascript:void(0);"
                                                                                                class="text-warning d-flex align-items-center hover-underline font-weight-bold"
                                                                                                @click="toggleApis(y)">
                                                                                                <i
                                                                                                    class="bx bx-list-ul me-1"></i>
                                                                                                {{ y.showApis ? 'Hide' :
                                                                                                    'View' }} API's
                                                                                                ({{ (y.planningApis ||
                                                                                                    y.apis || []).length }})
                                                                                            </a>
                                                                                        </small>
                                                                                    </div>
                                                                                    <span v-if="y.isBigProject"
                                                                                        class="badge bg-danger font-size-11">Big
                                                                                        Project</span>
                                                                                    <span
                                                                                        v-if="y.addressingClimateChange"
                                                                                        class="badge bg-success font-size-11">Climate
                                                                                        Change</span>
                                                                                    <span v-if="y.isAdaptation"
                                                                                        class="badge bg-info font-size-11">Adaptation</span>
                                                                                </div>
                                                                                <small v-if="y.expectedOutput"
                                                                                    class="d-block text-muted mt-1">
                                                                                    Expected Output: {{ y.expectedOutput
                                                                                    }}
                                                                                </small>
                                                                            </div>
                                                                        </div>

                                                                        <!-- Target Header Right: Cascaded Target & Quarters Table -->
                                                                        <div class="d-flex"
                                                                            v-if="y.cascadedTargetText || (y.q1 !== undefined && y.q1 !== null)">
                                                                            <table
                                                                                class="table table-sm table-bordered mb-0 text-center font-size-12">
                                                                                <thead>
                                                                                    <tr>
                                                                                        <th class="px-2"
                                                                                            v-if="y.cascadedTargetText">
                                                                                            Cascaded Target</th>
                                                                                        <th class="px-2"
                                                                                            v-if="y.q1 !== undefined && y.q1 !== null">
                                                                                            Q1</th>
                                                                                        <th class="px-2"
                                                                                            v-if="y.q2 !== undefined && y.q2 !== null">
                                                                                            Q2</th>
                                                                                        <th class="px-2"
                                                                                            v-if="y.q3 !== undefined && y.q3 !== null">
                                                                                            Q3</th>
                                                                                        <th class="px-2"
                                                                                            v-if="y.q4 !== undefined && y.q4 !== null">
                                                                                            Q4</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    <tr>
                                                                                        <td class="text-warning px-2"
                                                                                            v-if="y.cascadedTargetText">
                                                                                            {{
                                                                                                y.cascadedTargetText }}
                                                                                        </td>
                                                                                        <td class="text-warning px-2"
                                                                                            v-if="y.q1 !== undefined && y.q1 !== null">
                                                                                            {{
                                                                                                y.isPercentage ? `${y.q1}%`
                                                                                                    :
                                                                                                    y.q1 }}</td>
                                                                                        <td class="text-warning px-2"
                                                                                            v-if="y.q2 !== undefined && y.q2 !== null">
                                                                                            {{
                                                                                                y.isPercentage ? `${y.q2}%`
                                                                                                    :
                                                                                                    y.q2 }}</td>
                                                                                        <td class="text-warning px-2"
                                                                                            v-if="y.q3 !== undefined && y.q3 !== null">
                                                                                            {{
                                                                                                y.isPercentage ? `${y.q3}%`
                                                                                                    :
                                                                                                    y.q3 }}</td>
                                                                                        <td class="text-warning px-2"
                                                                                            v-if="y.q4 !== undefined && y.q4 !== null">
                                                                                            {{
                                                                                                y.isPercentage ? `${y.q4}%`
                                                                                                    :
                                                                                                    y.q4 }}</td>
                                                                                    </tr>
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </div>

                                                                    <!-- List of APIs Section (Visible when View API's is clicked) -->
                                                                    <div v-if="y.showApis" class="mt-3">
                                                                        <hr class="my-2" />
                                                                        <div
                                                                            class="d-flex justify-content-between align-items-center mb-2">
                                                                            <h6 class="mb-0 text-white font-size-14">
                                                                                <i class="bx bx-file me-1"></i>List of
                                                                                API's
                                                                            </h6>
                                                                            <a href="javascript:void(0);"
                                                                                class="font-size-18 text-white"
                                                                                @click="y.showApis = false">
                                                                                <i class="bx bx-x"></i>
                                                                            </a>
                                                                        </div>

                                                                        <div class="text-center py-3"
                                                                            v-if="!(y.planningApis || y.apis || []).length">
                                                                            <span class="text-muted">No API(s) found for
                                                                                this target.</span>
                                                                        </div>

                                                                        <div class="table-responsive" v-else>
                                                                            <table
                                                                                class="table table-bordered table-sm align-middle mb-0 font-size-13">
                                                                                <thead class="table-dark">
                                                                                    <tr>
                                                                                        <th>API Description</th>
                                                                                        <th width="170px"
                                                                                            class="text-end">
                                                                                            Total Amount</th>
                                                                                        <th width="280px"
                                                                                            class="text-center">Action /
                                                                                            Status</th>
                                                                                    </tr>
                                                                                </thead>
                                                                                <tbody>
                                                                                    <template
                                                                                        v-for="w in (y.planningApis || y.apis || [])">
                                                                                        <tr :key="`api-${w.id}`">
                                                                                            <td>
                                                                                                <div
                                                                                                    class="position-relative">
                                                                                                    <RawData :data="w"
                                                                                                        v-if="$store.state.decrypt.isAdmin()" />
                                                                                                    <span
                                                                                                        class="text-white font-weight-bold">{{
                                                                                                            w.name }}</span>
                                                                                                    <span
                                                                                                        v-if="$store.state.decrypt.isAdmin()"
                                                                                                        class="ms-1 cursor-pointer"
                                                                                                        title="API Logs"
                                                                                                        @click.stop="$emit('show-api-logs', w)">
                                                                                                        <i
                                                                                                            class="bx bx-receipt text-info"></i>
                                                                                                    </span>
                                                                                                </div>
                                                                                                <small
                                                                                                    class="d-block text-muted"
                                                                                                    v-if="w.description">
                                                                                                    [{{ w.description
                                                                                                    }}]
                                                                                                </small>
                                                                                                <!-- API Action Links -->
                                                                                                <div
                                                                                                    class="d-flex gap-2 flex-wrap mt-1">
                                                                                                    <small>
                                                                                                        <a href="javascript:void(0);"
                                                                                                            class="text-green hover-underline"
                                                                                                            @click.stop="toggleResources(w)">
                                                                                                            [ {{
                                                                                                                w.showResources
                                                                                                                    ? 'Hide' :
                                                                                                                    'View' }}
                                                                                                            Resources ]
                                                                                                        </a>
                                                                                                    </small>
                                                                                                    <small>
                                                                                                        <a href="javascript:void(0);"
                                                                                                            class="text-yellow hover-underline"
                                                                                                            @click.stop="$emit('show-api-attachments', w)">
                                                                                                            [ View
                                                                                                            Attachments
                                                                                                            ]
                                                                                                        </a>
                                                                                                    </small>
                                                                                                    <small>
                                                                                                        <a href="javascript:void(0);"
                                                                                                            class="text-white hover-underline"
                                                                                                            @click.stop="$emit('view-proposal-data', { props: w, status: type, du: du, campus: campus })">
                                                                                                            [ View
                                                                                                            Proposal
                                                                                                            ]
                                                                                                        </a>
                                                                                                    </small>
                                                                                                    <small>
                                                                                                        <a href="javascript:void(0);"
                                                                                                            class="text-info hover-underline"
                                                                                                            @click.stop="$emit('add-comment', { proposal: w, proposalId: w.id, du: du, campus: campus })">
                                                                                                            [ Add
                                                                                                            Comment ]
                                                                                                        </a>
                                                                                                    </small>
                                                                                                </div>
                                                                                            </td>
                                                                                            <td
                                                                                                class="text-end font-weight-bold text-warning align-top pt-2">
                                                                                                {{
                                                                                                    formatCurrency(w.total)
                                                                                                }}
                                                                                            </td>
                                                                                            <td class="align-top pt-2">
                                                                                                <template
                                                                                                    v-if="hasStatus(w, 'return')">
                                                                                                    <div
                                                                                                        class="text-center">
                                                                                                        <span
                                                                                                            class="badge bg-warning text-dark font-size-12">RETURNED</span>
                                                                                                    </div>
                                                                                                </template>
                                                                                                <template v-else>
                                                                                                    <div
                                                                                                        class="d-flex flex-column">
                                                                                                        <label
                                                                                                            class="mb-1 font-size-11">
                                                                                                            Status:
                                                                                                            <span
                                                                                                                class="ms-1 text-white font-weight-bold"
                                                                                                                v-if="w.status && w.status.toUpperCase() !== 'DRAFT'">
                                                                                                                {{
                                                                                                                    w.status
                                                                                                                }}
                                                                                                            </span>
                                                                                                            <span v-else
                                                                                                                class="ms-1 text-muted">
                                                                                                                Draft
                                                                                                            </span>
                                                                                                        </label>
                                                                                                        <div
                                                                                                            class="d-flex align-items-center gap-1">
                                                                                                            <select
                                                                                                                class="form-select form-select-sm"
                                                                                                                v-model="w.statusUpdate">
                                                                                                                <option
                                                                                                                    value="">
                                                                                                                    --Select
                                                                                                                    status--
                                                                                                                </option>
                                                                                                                <template
                                                                                                                    v-if="isActive || isShortListed || isParked">
                                                                                                                    <option
                                                                                                                        value="approve"
                                                                                                                        class="text-green">
                                                                                                                        Approve
                                                                                                                    </option>
                                                                                                                </template>
                                                                                                                <option
                                                                                                                    value="return"
                                                                                                                    class="text-warning">
                                                                                                                    Return
                                                                                                                </option>
                                                                                                                <template
                                                                                                                    v-if="isActive || isParked">
                                                                                                                    <option
                                                                                                                        value="shortlist">
                                                                                                                        Add
                                                                                                                        to
                                                                                                                        Shortlist
                                                                                                                    </option>
                                                                                                                </template>
                                                                                                                <template
                                                                                                                    v-if="isActive || isShortListed || isApproved">
                                                                                                                    <option
                                                                                                                        value="park">
                                                                                                                        Park
                                                                                                                    </option>
                                                                                                                </template>
                                                                                                            </select>
                                                                                                            <button
                                                                                                                v-if="w.statusUpdate"
                                                                                                                type="button"
                                                                                                                class="btn btn-sm"
                                                                                                                :class="{
                                                                                                                    'btn-success': w.statusUpdate === 'approve',
                                                                                                                    'btn-warning': w.statusUpdate === 'return',
                                                                                                                    'btn-info': w.statusUpdate === 'shortlist',
                                                                                                                    'btn-secondary': w.statusUpdate === 'park'
                                                                                                                }"
                                                                                                                @click="updateProposalStatus({
                                                                                                                    proposalId: w.id,
                                                                                                                    du: du,
                                                                                                                    campus: campus,
                                                                                                                    status: w.statusUpdate
                                                                                                                })">
                                                                                                                <i
                                                                                                                    class="bx bx-check"></i>
                                                                                                            </button>
                                                                                                        </div>
                                                                                                    </div>
                                                                                                </template>
                                                                                            </td>
                                                                                        </tr>

                                                                                        <!-- Expandable Resource Row when View Resources is active -->
                                                                                        <tr v-if="w.showResources"
                                                                                            :key="`api-res-${w.id}`">
                                                                                            <td colspan="3"
                                                                                                class="bg-dark p-2">
                                                                                                <div
                                                                                                    class="table-responsive">
                                                                                                    <ProposalItemExpenditure
                                                                                                        :api="w"
                                                                                                        :view-from="viewFrom"
                                                                                                        :department="department"
                                                                                                        @change-expenditure="$emit('change-expenditure', $event)"
                                                                                                        @assign-fund-source="$emit('assign-fund-source', { ...$event, campus: campus, campusId: campus && campus.id ? campus.id : null })" />
                                                                                                </div>
                                                                                            </td>
                                                                                        </tr>
                                                                                    </template>
                                                                                </tbody>
                                                                            </table>
                                                                        </div>
                                                                    </div>
                                                                </b-card-header>
                                                            </div>
                                                        </b-card-body>
                                                    </div>
                                                </b-tab>
                                            </div>
                                        </b-tabs>
                                        <div class="position-absolute" :style="{ top: 0, right: '5px' }">
                                            <button class="btn btn-warning" @click="viewAttachments(opcr.targets)"><i
                                                    class="bx bx-paperclip"></i> View
                                                Attachments</button>
                                        </div>
                                    </div>
                                </div>
                            </b-card-body>
                        </b-collapse>
                    </b-card>
                </div>
            </template>
        </b-card>
        <RemarksModal ref="remarksModal" :modalId="remarksModalId" @submit="submitReturnOpcr" />
    </div>
</template>

<style scoped>
.rotate {
    transition: transform 0.2s ease-in-out;
}

.rotate-90 {
    transform: rotate(90deg);
}

.hover-underline:hover {
    text-decoration: underline;
}

.cursor-pointer {
    cursor: pointer;
}

.w-300px {
    width: 300px;
}
</style>
